import React from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import BlobImage from 'components/blobImage';
import { StringHelpers } from '@salesperformanceint/e4shared';
import { metricLegend, metricFormatFromId, competencyLegend, goalLegend } from '../_base';
import { Popover, PopoverBody } from 'reactstrap';
import { getGroupIcon } from '../leaderboard/board';
import posthog from 'posthog-js';

export const Staff = ({ data, item, id, history }) => {
    const [height, setHeight] = React.useState(0);
    const [width, setWidth] = React.useState(0);
    const [imageRadius, setImageRadius] = React.useState(10);
    const [open, setOpen] = React.useState(null);
    const [legendX, setLegendX] = React.useState('x-axis');
    const [legendY, setLegendY] = React.useState('y-axis');
    const [formatX, setFormatX] = React.useState('Q');
    const [formatY, setFormatY] = React.useState('Q');

    const user = useSelector(state => state.user.user);
    const ratingRange = user.companySettings?.display?.ratingRange || 5;
    const metrics = useSelector(state => state.dashboard.metrics);
    const goals = useSelector(state => state.dashboard.goals);
    const competencies = useSelector(state => state.dashboard.competencies);

    const parsed = JSON.parse(item.data);

    parsed.groupBy = parsed.groupBy || 'P';

    const groupIcon = getGroupIcon(parsed.groupBy);

    const leftPadding = 60;
    const bottomPadding = 60;
    const rightPadding = 20;
    const topPadding = 20;

    const goalFormat = React.useCallback(
        id => {
            const goal = goals.find(g => g.id === id);
            if (!goal) return 'Q';
            const metric = metrics.find(m => !m.realTime && m.id === goal.metric_Id && m.integration === goal.integration);
            if (!metric) return 'Q';
            return metric.format;
        },
        [goals, metrics]
    );

    React.useEffect(() => {
        const container = document.getElementById(id);
        setHeight(container.offsetHeight - bottomPadding - topPadding);
        setWidth(container.offsetWidth - leftPadding - rightPadding);
        setImageRadius(item.w > 2 ? 15 : 10);
    }, [item.h, item.w]);

    React.useEffect(() => {
        if (item?.data) {
            if (parsed.type1 === 'M') {
                setLegendX(metricLegend(parsed.metric1, metrics, null));
                setFormatX(metricFormatFromId(parsed.metric1, metrics));
            } else if (parsed.type1 === 'C') {
                setLegendX(competencyLegend(parsed.competency1, competencies));
                setFormatX('R');
            } else {
                setLegendX(goalLegend(parsed.goal1, goals));
                setFormatX('P');
            }

            if (parsed.type2 === 'M') {
                setLegendY(metricLegend(parsed.metric2, metrics, null));
                setFormatY(metricFormatFromId(parsed.metric2, metrics));
            } else if (parsed.type2 === 'C') {
                setLegendY(competencyLegend(parsed.competency2, competencies));
                setFormatY('R');
            } else {
                setLegendY(goalLegend(parsed.goal2, goals));
                setFormatY('P');
            }
        }
    }, [item.data]);

    const processed = data[0].data.map((xData, i) => {
        let yData = data[1].data.find(d => d.id === xData.id) || { value: 0, percentile: 0 };
        let y = parsed.type2 === 'C' && parsed.competencyTrueScale2 ? (yData.value * 100) / ratingRange : yData.percentile;
        let x = parsed.type1 === 'C' && parsed.competencyTrueScale1 ? (xData.value * 100) / ratingRange : xData.percentile;
        return { xData, yData, x, y, i };
    });

    return (
        <div>
            {processed.map((d, i) => {
                const xData = d.xData;
                const yData = d.yData;
                const y = d.y;
                const x = d.x;
                const others = processed.filter(p => p.y === y && p.x === x && p.i !== i);
                const otherNames = others.reduce((all, c) => `${all}\n${c.xData.full_Name}`, '');
                return (
                    <div
                        key={`staff-${xData.id}`}
                        style={{
                            position: 'absolute',
                            bottom: `${bottomPadding + (height * y) / 100 - imageRadius}px`,
                            left: `${leftPadding + (width * x) / 100 - imageRadius}px`,
                        }}
                    >
                        <span className="pointer">
                            <button
                                className="btn-link"
                                onMouseOver={() => setOpen(xData.id)}
                                onFocus={() => setOpen(xData.id)}
                                onMouseOut={() => setOpen(null)}
                                onBlur={() => setOpen(null)}
                                onTouchStart={() => setOpen(xData.id)}
                                onTouchEnd={() => setOpen(null)}
                                onClick={() => {
                                    posthog.capture('widget-profile-click');
                                    history.push(`/develop/performance-dashboard/${xData.id}`);
                                }}
                            >
                                {['P', 'M'].some(x => x === parsed.groupBy) ? (
                                    <BlobImage
                                        id={`image-${id}-${xData.id}`}
                                        folder="avatar"
                                        initials={xData.initials}
                                        src={xData.avatar}
                                        className="img-circle"
                                        alt={xData.full_Name}
                                        style={{
                                            border: 'solid 1px #a0a0a0',
                                            borderRadius: '50%',
                                            width: `${imageRadius * 2}px`,
                                            height: `${imageRadius * 2}px`,
                                        }}
                                    />
                                ) : (
                                    <i
                                        id={`image-${id}-${xData.id}`}
                                        className={`fa ${groupIcon} mr10 ${imageRadius === 15 ? 'text-lg' : ''}`}
                                        style={{
                                            border: 'solid 1px #a0a0a0',
                                            padding: '3px',
                                            borderRadius: '3px',
                                            backgroundColor: '#fff',
                                        }}
                                    />
                                )}
                            </button>
                            {others.length > 0 && (
                                <label
                                    style={{ position: 'absolute', top: '-2px', left: '22px', fontSize: '8px' }}
                                    className="label label-primary"
                                >
                                    {others.length + 1}
                                </label>
                            )}
                        </span>
                        <Popover isOpen={open === xData.id} target={`image-${id}-${xData.id}`}>
                            <PopoverBody className="popover-body copy">
                                <div className="flex">
                                    <div className="flex-grow">
                                        <div>
                                            <h3 style={{ marginTop: '0' }}>
                                                {xData.full_Name}
                                                {otherNames}
                                            </h3>
                                        </div>
                                        <div className="mb10">
                                            {legendX}:{' '}
                                            {StringHelpers.formatMetric(formatX, xData.value, user.currencySymbol)}
                                            {parsed.type1 === 'G' ? (
                                                <div className="text-sm">
                                                    (
                                                    {StringHelpers.formatMetric(
                                                        goalFormat(parsed.goal1),
                                                        xData.achieved,
                                                        user.currencySymbol
                                                    )}{' '}
                                                    /{' '}
                                                    {StringHelpers.formatMetric(
                                                        goalFormat(parsed.goal1),
                                                        xData.quota,
                                                        user.currencySymbol
                                                    )}
                                                    )
                                                </div>
                                            ) : (
                                                <div className="text-sm">
                                                    ({xData.percentile}
                                                    {StringHelpers.getOrdinalSuffix(xData.percentile.toFixed())} percentile)
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            {legendY}:{' '}
                                            {StringHelpers.formatMetric(formatY, yData.value, user.currencySymbol)}
                                            {parsed.type2 === 'G' ? (
                                                <div className="text-sm">
                                                    (
                                                    {StringHelpers.formatMetric(
                                                        goalFormat(parsed.goal2),
                                                        yData.achieved,
                                                        user.currencySymbol
                                                    )}{' '}
                                                    /{' '}
                                                    {StringHelpers.formatMetric(
                                                        goalFormat(parsed.goal2),
                                                        yData.quota,
                                                        user.currencySymbol
                                                    )}
                                                    )
                                                </div>
                                            ) : (
                                                <div className="text-sm">
                                                    ({yData.percentile}
                                                    {StringHelpers.getOrdinalSuffix(yData.percentile.toFixed())} percentile)
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </PopoverBody>
                        </Popover>
                    </div>
                );
            })}
        </div>
    );
};

export default withRouter(Staff);
